import { useEffect, useState } from "react";
import { Link, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import { ReceiptThumb } from "@/pages/personal/ReimbursementReceiptThumb";
import { WellnessAllotmentNotice } from "@/pages/personal/WellnessAllotmentNotice";
import {
  approveAccountingReimbursement,
  describeWellnessAllotment,
  fetchAccountingReimbursement,
  fetchAccountingReimbursements,
  fetchExpenseCategories,
  formatReimbursementMoney,
  markAccountingReimbursementPaid,
  rejectAccountingReimbursement,
  reimbursementKindLabel,
  reimbursementStatusLabel,
  reimbursementSubmitterLabel,
  setAccountingWellnessSchedule,
  updateAccountingLineCategory,
  type Reimbursement,
  type WellnessSchedule,
} from "@/pages/personal/reimbursementApi";

type SortKey = "submitted" | "person" | "kind" | "amount" | "status";

function reimbursementAmount(row: Reimbursement): number {
  return row.amountPaid ?? row.amountRequested ?? 0;
}

const REIMBURSEMENT_COLUMNS: HubDataTableColumn<Reimbursement, SortKey>[] = [
  {
    id: "submitted",
    header: "Submitted",
    sortKey: "submitted",
    overflow: "nowrap",
    sortValue: (row) => row.submittedAt ?? row.createdAt,
    cell: (row) => (row.submittedAt ?? row.createdAt).slice(0, 10),
  },
  {
    id: "person",
    header: "Person",
    sortKey: "person",
    sortValue: (row) => reimbursementSubmitterLabel(row),
    cell: (row) => (
      <Link className="font-medium text-brand-700 hover:underline" to={`/accounting/reimbursements/${row.id}`}>
        {reimbursementSubmitterLabel(row)}
      </Link>
    ),
  },
  {
    id: "kind",
    header: "Kind",
    sortKey: "kind",
    sortValue: (row) => row.kind,
    cell: (row) => reimbursementKindLabel(row.kind),
  },
  {
    id: "amount",
    header: "Amount",
    sortKey: "amount",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => reimbursementAmount(row),
    cell: (row) => formatReimbursementMoney(reimbursementAmount(row)) || "—",
  },
  {
    id: "status",
    header: "Status",
    sortKey: "status",
    sortValue: (row) => row.status,
    cell: (row) => reimbursementStatusLabel(row.status),
  },
];

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Something went wrong.";
}

function todayIso(): string {
  return new Date().toLocaleDateString("en-CA");
}

function AccountingList(): JSX.Element {
  const navigate = useNavigate();
  const [status, setStatus] = useState("submitted");
  const query = useQuery({
    queryKey: ["accounting", "reimbursements", status],
    queryFn: () => fetchAccountingReimbursements(status),
  });
  const rows = query.data ?? [];

  return (
    <div className="flex min-h-0 flex-col gap-4">
      <PageTitleWithHelp hoverHint="Review staff expense, per diem, mileage, and wellness requests. Approve them, send them back, or mark them reimbursed.">
        Reimbursements
      </PageTitleWithHelp>
      <Card className="p-4">
        <label className="flex flex-col gap-1 text-sm text-slate-700">
          Status
          <select
            className="h-10 max-w-xs rounded-md border border-slate-300 bg-white px-3"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
            <option value="reimbursed">Reimbursed</option>
            <option value="rejected">Needs changes</option>
            <option value="all">All except drafts</option>
          </select>
        </label>
      </Card>
      <HubDataTable
        title="Requests"
        columns={REIMBURSEMENT_COLUMNS}
        rows={rows}
        rowKey={(row) => row.id}
        loading={query.isLoading}
        loadingMessage="Loading requests…"
        error={query.error ? errorMessage(query.error) : null}
        empty="No rows match these filters."
        onRowClick={(row) => navigate(`/accounting/reimbursements/${row.id}`)}
      />
    </div>
  );
}

function DetailField({ label, value }: { label: string; value: string }): JSX.Element | null {
  if (value.trim() === "") return null;
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 whitespace-pre-wrap text-slate-900">{value}</dd>
    </div>
  );
}

function AccountingDetail(): JSX.Element {
  const { id = "" } = useParams();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["accounting", "reimbursements", "detail", id],
    queryFn: () => fetchAccountingReimbursement(id),
    enabled: id !== "",
  });
  const categoriesQuery = useQuery({
    queryKey: ["accounting", "reimbursement-categories"],
    queryFn: () => fetchExpenseCategories("accounting"),
  });
  const [note, setNote] = useState("");
  const [amountPaid, setAmountPaid] = useState("");
  const [approveAmount, setApproveAmount] = useState("");
  const [paidOn, setPaidOn] = useState(todayIso());
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (query.data == null) return;
    setAmountPaid(
      query.data.amountPaid != null
        ? String(query.data.amountPaid)
        : query.data.amountApproved != null
          ? String(query.data.amountApproved)
          : query.data.amountRequested != null
            ? String(query.data.amountRequested)
            : "",
    );
    if (query.data.kind === "wellness" && query.data.wellnessAllotment != null) {
      const described = describeWellnessAllotment(query.data.wellnessAllotment, query.data.amountRequested ?? 0);
      setApproveAmount(described.suggestedApproveUsd > 0 ? String(described.suggestedApproveUsd) : "");
    }
    setPaidOn(query.data.paidOn ?? todayIso());
  }, [query.data]);

  const row = query.data;

  const refresh = async (next: Reimbursement) => {
    queryClient.setQueryData(["accounting", "reimbursements", "detail", id], next);
    await queryClient.invalidateQueries({ queryKey: ["accounting", "reimbursements"] });
  };

  const approveMutation = useMutation({
    mutationFn: () => {
      if (row?.kind === "wellness") {
        return approveAccountingReimbursement(id, Number.parseFloat(approveAmount));
      }
      return approveAccountingReimbursement(id);
    },
    onSuccess: (next) => {
      setMessage("Approved.");
      void refresh(next);
    },
    onError: (cause) => setMessage(errorMessage(cause)),
  });
  const rejectMutation = useMutation({
    mutationFn: () => rejectAccountingReimbursement(id, note),
    onSuccess: (next) => {
      setMessage("Sent back to the employee.");
      void refresh(next);
    },
    onError: (cause) => setMessage(errorMessage(cause)),
  });
  const payMutation = useMutation({
    mutationFn: () => markAccountingReimbursementPaid(id, {
      amountPaid: Number.parseFloat(amountPaid),
      paidOn,
      note: note.trim() === "" ? undefined : note.trim(),
    }),
    onSuccess: (next) => {
      setMessage("Marked reimbursed.");
      void refresh(next);
    },
    onError: (cause) => setMessage(errorMessage(cause)),
  });

  if (query.isLoading) return <p className="text-sm text-slate-600">Loading request…</p>;
  if (query.error || row == null) return <p className="text-sm text-red-700">{errorMessage(query.error ?? new Error("Not found."))}</p>;

  const yesNo = (value: boolean | null) => (value == null ? "" : value ? "Yes" : "No");

  return (
    <div className="space-y-4">
      <PageTitleWithHelp
        breadcrumbLabel={reimbursementSubmitterLabel(row)}
        hoverHint="Approve the request, send it back with a note, or record the amount and date paid."
      >
        {reimbursementKindLabel(row.kind)} reimbursement
      </PageTitleWithHelp>
      <p className="text-sm text-slate-600">
        {reimbursementSubmitterLabel(row)}
        {row.submitterEmail ? ` · ${row.submitterEmail}` : ""} · {reimbursementStatusLabel(row.status)}
      </p>
      {message ? <p className="text-sm text-slate-700">{message}</p> : null}
      <Card className="grid gap-4 p-4 sm:grid-cols-2">
        <DetailField label="Start" value={row.startOn ?? ""} />
        <DetailField label="End" value={row.endOn ?? ""} />
        <DetailField label="Date of expense" value={row.incurredOn ?? ""} />
        <DetailField label="Amount requested" value={formatReimbursementMoney(row.amountRequested)} />
        <DetailField label="Amount approved" value={formatReimbursementMoney(row.amountApproved)} />
        <DetailField label="Destination" value={row.destination ?? ""} />
        <DetailField label="Purpose" value={row.purpose ?? ""} />
        <DetailField label="Started before 9 am" value={yesNo(row.departedBefore9am)} />
        <DetailField label="Ended after 5 pm" value={yesNo(row.returnedAfter5pm)} />
        <DetailField label="Company-paid meals and lodging" value={row.companyPaidMealsLodging ?? ""} />
        <DetailField label="Advance" value={formatReimbursementMoney(row.advanceAmount)} />
        <DetailField label="Miles" value={row.miles == null ? "" : String(row.miles)} />
        <DetailField label="Explanation" value={row.explanation ?? ""} />
        <DetailField label="Amount paid" value={formatReimbursementMoney(row.amountPaid)} />
        <DetailField label="Date paid" value={row.paidOn ?? ""} />
        <DetailField label="Accounting note" value={row.accountingNote ?? ""} />
      </Card>
      {row.lines.length > 0 ? (
        <Card className="space-y-3 p-4">
          <h2 className="text-sm font-semibold text-slate-900">Categories</h2>
          {row.lines.map((line) => (
            <div key={line.id} className="grid gap-2 border-t border-slate-100 pt-3 sm:grid-cols-[1fr_8rem]">
              <label className="block text-sm text-slate-700">
                {line.merchant ?? `Line ${line.lineNumber}`}
                <select
                  className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-2"
                  value={line.categoryKey ?? ""}
                  disabled={row.status === "reimbursed"}
                  onChange={(event) => {
                    void updateAccountingLineCategory(row.id, line.id, event.target.value)
                      .then((next) => refresh(next))
                      .catch((cause: unknown) => setMessage(errorMessage(cause)));
                  }}
                >
                  {(categoriesQuery.data ?? []).map((category) => (
                    <option key={category.key} value={category.key}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="self-end text-right text-sm font-medium tabular-nums">{formatReimbursementMoney(line.amount)}</div>
            </div>
          ))}
        </Card>
      ) : null}
      {row.kind === "wellness" && row.wellnessAllotment ? (
        <div className="space-y-3">
          <WellnessAllotmentNotice allotment={row.wellnessAllotment} thisAmount={row.amountRequested ?? 0} />
          {row.status !== "reimbursed" ? (
            <label className="block text-sm text-slate-700">
              Schedule
              <select
                className="mt-1 h-10 w-full max-w-xs rounded-md border border-slate-300 bg-white px-3"
                value={row.wellnessAllotment.schedule ?? ""}
                onChange={(event) => {
                  const schedule = event.target.value;
                  if (schedule !== "full_time" && schedule !== "part_time") return;
                  void setAccountingWellnessSchedule(row.id, schedule as WellnessSchedule)
                    .then((next) => refresh(next))
                    .catch((cause: unknown) => setMessage(errorMessage(cause)));
                }}
              >
                <option value="">Not set (full-time cap)</option>
                <option value="full_time">Full-time ($800 per year)</option>
                <option value="part_time">Part-time ($400 per year)</option>
              </select>
            </label>
          ) : null}
        </div>
      ) : null}
      <div className="space-y-3">
        {row.receipts.map((receipt) => (
          <Card key={receipt.id} className="p-4">
            <ReceiptThumb scope="accounting" reimbursementId={row.id} receipt={receipt} />
          </Card>
        ))}
      </div>
      {row.status !== "reimbursed" ? (
        <Card className="space-y-3 p-4">
          <label className="block space-y-1 text-sm text-slate-700">
            Note
            <textarea className="min-h-20 w-full rounded-md border border-slate-300 px-3 py-2 text-base" value={note} onChange={(event) => setNote(event.target.value)} />
          </label>
          <div className="flex flex-wrap gap-2">
            {row.status === "submitted" || row.status === "rejected" ? (
              <div className="flex flex-wrap items-end gap-2">
                {row.kind === "wellness" ? (
                  <label className="block text-sm text-slate-700">
                    Amount to approve
                    <input
                      className="mt-1 h-10 w-40 rounded-md border border-slate-300 px-3 text-base"
                      inputMode="decimal"
                      value={approveAmount}
                      onChange={(event) => setApproveAmount(event.target.value)}
                    />
                  </label>
                ) : null}
                <Button type="button" isLoading={approveMutation.isPending} onClick={() => approveMutation.mutate()}>
                  {row.kind === "wellness" && row.amountApproved == null && row.wellnessAllotment && (row.amountRequested ?? 0) > row.wellnessAllotment.remainingUsd
                    ? "Approve partial amount"
                    : "Approve"}
                </Button>
              </div>
            ) : null}
            {row.status === "submitted" || row.status === "approved" ? (
              <Button type="button" variant="secondary" isLoading={rejectMutation.isPending} onClick={() => rejectMutation.mutate()}>
                Send back
              </Button>
            ) : null}
          </div>
          {row.status === "approved" ? (
            <div className="grid gap-3 border-t border-slate-100 pt-3 sm:grid-cols-2">
              <label className="block text-sm text-slate-700">
                Amount paid
                <input className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-base" inputMode="decimal" value={amountPaid} onChange={(event) => setAmountPaid(event.target.value)} />
              </label>
              <label className="block text-sm text-slate-700">
                Date paid
                <input className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-base" type="date" value={paidOn} onChange={(event) => setPaidOn(event.target.value)} />
              </label>
              <Button type="button" variant="success" className="sm:col-span-2" isLoading={payMutation.isPending} onClick={() => payMutation.mutate()}>
                Mark reimbursed
              </Button>
            </div>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
}

export function EmployeeReimbursementsPage(): JSX.Element {
  return (
    <Routes>
      <Route index element={<AccountingList />} />
      <Route path=":id" element={<AccountingDetail />} />
    </Routes>
  );
}
